import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@mui/material";
import axios from "axios";
import { API_BASE_URL } from "../../config";
// import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

export default function ManageCoupons() {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);


  useEffect(() => {
    fetchAllCoupons();
  }, []);



  const fetchAllCoupons = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/coupons`);
      if (response.data.success === true) {
        const rawCoupons = response.data.data || [];

        const parsedCoupons = rawCoupons.map((item) => ({
          id: item.id,
          coupon_code: item.coupon_code || item.code || "-",
          discount_type: item.discount_type || item.type || "percentage",
          discount_value: Number(item.discount_value || item.value || 0),
          start_date: item.start_date || item.start || null,
          end_date: item.end_date || item.end || null,
          usage_limit: item.usage_limit ?? 0,
          used_count: item.used_count ?? 0,
          status: item.status === 1 ? 1 : 0,
          created_at: item.created_at || "-",
          updated_at: item.updated_at || "-",
          status_text: item.status === 1 ? "Active" : "Inactive",
        }));

        setCoupons(parsedCoupons);
        setTotalPages(Math.ceil(parsedCoupons.length / rowsPerPage));
      } else {
        setCoupons([]);
        setError("Failed to fetch coupons");
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setError("Error fetching coupons");
    } finally {
      setIsLoading(false);
    }
  };


  const handleRowsChange = (e) => {
    const newRows = Number(e.target.value);
    setRowsPerPage(newRows);
    setCurrentPage(1);
    setTotalPages(Math.ceil(coupons.length / newRows));
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);


  // ✅ Format date for readability
  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  // ✅ Handle status change with SweetAlert2
  const handleStatusChange = async (couponId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? "Active" : "Inactive";

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to change the coupon status to ${statusText}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.put(
            `${API_BASE_URL}/update-coupon-status/${couponId}`,
            { status: newStatus }
          );

          if (response.data.success === true) {
            fetchAllCoupons();
            Swal.fire({
              title: "Updated!",
              text: response.data.message || "Coupon status has been updated.",
              icon: "success",
              timer: 2000,
              showConfirmButton: false
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: response.data.message || "Failed to update coupon status.",
              icon: "error"
            });
          }
        } catch (error) {
          console.error("Error updating coupon status:", error);
          Swal.fire({
            title: "Error!",
            text: "An error occurred while updating the coupon status.",
            icon: "error"
          });
        }
      }
    });
  };

  return (
    <>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18 m-0">Manage Coupons</h4>
                </div>

                <div className="card">
                  <div className="card-body rounded-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h4 className="card-title mb-2">Coupons List</h4>

                      <div className="d-flex gap-2 align-items-center">
                        <button
                          className="btn btn-outline-secondary d-flex align-items-center gap-2"
                          onClick={fetchAllCoupons}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm text-secondary"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <span>Loading...</span>
                            </>
                          ) : (
                            <>
                              <i className="fa fa-sync me-1"></i>
                              <span>Refresh</span>
                            </>
                          )}
                        </button>


                        <Link
                          type="button"
                          className="btn btn-success inner"
                          to={"/cms/add-coupon"}
                        >
                          Add Coupon <i className="fa fa-plus" />
                        </Link>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered table-hover align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Id</th>
                            <th>Coupon Code</th>
                            <th>Discount Type</th>
                            <th>Discount Value</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Usage Limit</th>
                            <th>Used Count</th>
                            <th>Status</th>
                            {/* <th>Created At</th> */}
                            {/* <th>Updated At</th> */}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            // 🔹 Skeleton Loader
                            Array(5)
                              .fill()
                              .map((_, index) => (
                                <tr key={index}>
                                  {Array(12)
                                    .fill()
                                    .map((__, i) => (
                                      <td key={i}>
                                        <Skeleton width={100} height={25} />
                                      </td>
                                    ))}
                                </tr>
                              ))
                          ) : error ? (
                            // 🔹 Error Message
                            <tr>
                              <td colSpan={12} className="text-danger text-center">
                                {error}
                              </td>
                            </tr>
                          ) : coupons.length === 0 ? (
                            // 🔹 No Coupons Message
                            <tr>
                              <td colSpan={12} className="text-center">
                                No coupons available.
                              </td>
                            </tr>
                          ) : (
                            // 🔹 Render Coupons
                            coupons.map((coupon) => (
                              <tr key={coupon?.id}>
                                <td><strong>{coupon?.id}</strong></td>
                                <td> <b>{coupon?.coupon_code}</b> </td>
                                <td>{coupon?.discount_type}</td>
                                <td>{coupon?.discount_value}</td>
                                <td>{formatDate(coupon?.start_date)}</td>
                                <td>{formatDate(coupon?.end_date)}</td>
                                <td>{coupon?.usage_limit ?? "-"}</td>
                                <td>{coupon?.used_count ?? 0}</td>
                                <td>
                                  <span
                                    role="button"
                                    onClick={() => handleStatusChange(coupon?.id, coupon?.status)}
                                    className={`badge text-uppercase p-2 w-100 bg-${coupon?.status === 1 ? "success shadow-sm" : "danger"}`}
                                    style={{ cursor: "pointer" }}
                                    title={`Click to ${coupon?.status === 1 ? "Deactivate" : "Activate"}`}
                                  >
                                    {coupon?.status === 1 ? "Active" : "Inactive"}
                                  </span>
                                </td>


                                {/* <td>{formatDate(coupon?.created_at)}</td> */}
                                {/* <td>{formatDate(coupon?.updated_at)}</td> */}
                                <td>
                                  {/* <button
                                    className={`btn btn-sm me-2 btn-${coupon?.status === 1 ? "warning" : "success"}`}
                                    onClick={() => handleStatusChange(coupon?.id, coupon?.status)}
                                    title={coupon?.status === 1 ? "Deactivate" : "Activate"}
                                  >
                                    <i className={`fa fa-${coupon?.status === 1 ? "times-circle" : "check-circle"}`} />
                                  </button> */}
                                  {/* <button className="btn btn-primary btn-sm">
                                    <i className="fa fa-pen" />
                                  </button> */}

                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => navigate(`/cms/edit-coupon/${coupon?.id}`)}
                                    title="Edit Coupon"
                                  >
                                    <i className="fa fa-pen" />
                                  </button>


                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>

                      </table>
                    </div>

                    {/* Pagination controls */}
                    <div className="d-flex justify-content-between align-items-center my-4">
                      <div className="d-flex align-items-center">
                        <label className="me-2 fw-semibold">Rows per page:</label>
                        <select
                          className="form-select form-select-sm"
                          value={rowsPerPage}
                          onChange={handleRowsChange}
                          style={{ width: "80px" }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                        </select>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                        >
                          Prev
                        </button>

                        <span className="fw-semibold">
                          Page {currentPage} of {totalPages} ({coupons.length} items)
                        </span>

                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
